/* eslint-disable no-underscore-dangle */
import React from 'react';
import {
  FieldProps,
  ErrorMessage,
} from 'formik';

import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  Icon,
  Select,
} from 'web-components';
import {
  Flex,
} from 'web-styled';

import * as S from './styled';

const mapState = state => ({
  options: coreSelectors.getTagOptions(state),
});

export type Props = {
  flex: string;
  width?: string;
  spacing?: number;
  label?: string;
  color?: string;
} & FieldProps;

const TagField = ({
  label,
  field,
  form: { touched, errors, setFieldValue },
  placeholder,
  flex,
  width,
  spacing,
  color,
  onChange,
}: Props) => {
  const iserror = (touched[field.name] && errors[field.name]);
  const { options } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();

  return (
    <Flex
      column
      width={width}
      spacing={spacing}
      flex={flex}
    >
      <S.Label
        htmlFor={`${field.name}-id`}
        color={color}
      >
        {label}
      </S.Label>

      <Select
        value=""
        creatable
        options={options}
        placeholder={placeholder}
        createOptionPosition="first"
        formatCreateLabel={userInput => (
          <Flex align="center" spacing={10}>
            <S.PlusCircle />
            <span>
              {`Create tag "${userInput}"`}
            </span>
            <S.Keystroke>
              Enter
            </S.Keystroke>
          </Flex>
        )}
        theme={{
          borderRadius: '0.25rem',
        }}
        styles={{
          container: provided => ({
            ...provided,
            width: '100%',
          }),
          control: provided => ({
            ...provided,
            height: '40px',
            minHeight: '34px',
            boxShadow: 'none',
            borderColor: iserror ? '#de350b' : '#ddd',
            borderWidth: iserror ? '2px' : '1px',
            '&:focus': {
              borderColor: iserror ? '#de350b' : '#ddd',
            },
            '&:hover': {
              borderColor: iserror ? '#de350b' : '#ddd',
            },
          }),
        }}
        onChange={(option) => {
          if (option?.value) {
            const newTags = field.value
              ? [
                ...new Set([
                  ...field.value.split(','),
                  option.value,
                ]),
              ].join(',')
              : option.value;

            if (option.__isNew__) {
              dispatch(
                coreActions.addResources({
                  resourceType: 'tags',
                  resources: [{
                    id: option.value,
                    name: option.value,
                  }],
                }),
              );
            }

            setFieldValue(
              field.name,
              newTags,
            );
          }

          if (onChange) onChange(option);
        }}
      />
      {field.value && (
        <Flex
          wrap="wrap"
          align="center"
          spacing={6}
        >
          {field.value.split(',').sort().map(tag => (
            <S.Tag key={tag}>
              <span>
                {tag}
              </span>
              <Flex
                clickable
                onClick={() => {
                  setFieldValue(
                    field.name,
                    field.value
                      .split(',')
                      .filter(t => t !== tag)
                      .join(','),
                  );
                }}
              >
                <Icon
                  size="13px"
                  type="fas fa-times"
                />
              </Flex>
            </S.Tag>
          ))}
        </Flex>
      )}
      <ErrorMessage
        name={field.name}
        component={S.Error}
      />
    </Flex>
  );
};

TagField.defaultProps = {
  label: 'Tags',
  width: '100%',
  color: 'primary',
  spacing: 10,
  placeholder: 'Type here to create or select tags...',
};

export default TagField;
